import React, { useEffect, useState } from 'react';
import { ProjectRow } from '../../../lib/types';
import styles from './forum-agenda.module.css';

interface ForumItem extends ProjectRow {
  voteCount?: number;
  commentCount?: number;
  assessmentScore?: number;
}

export default function ForumAgenda() {
  const [ideas, setIdeas] = useState<ForumItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedIdea, setSelectedIdea] = useState<ForumItem | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchForumIdeas();
  }, []);

  const fetchForumIdeas = async () => {
    try {
      setLoading(true);
      setError('');

      // Get all projects with ReadyForGovernance status
      const projectsRes = await fetch('/projects');
      const projectsData = await projectsRes.json();

      if (projectsData.success && projectsData.data) {
        const forumIdeas = projectsData.data.filter((p: ProjectRow) =>
          p.Status === 'ReadyForGovernance'
        );

        // Fetch votes and comments for each idea
        const enrichedIdeas = await Promise.all(
          forumIdeas.map(async (idea: ProjectRow) => {
            try {
              const votesRes = await fetch(`/projects/${idea.ProjectId}/votes`);
              const votesData = await votesRes.json();
              const voteCount = votesData.data?.length || 0;

              const commentsRes = await fetch(`/projects/${idea.ProjectId}/comments`);
              const commentsData = await commentsRes.json();
              const commentCount = commentsData.data?.length || 0;

              const assessmentRes = await fetch(`/projects/${idea.ProjectId}/assessment`);
              const assessmentData = await assessmentRes.json();
              const assessmentScore = assessmentData.data?.Score || 0;

              return {
                ...idea,
                voteCount,
                commentCount,
                assessmentScore,
              };
            } catch (err) {
              console.error(`Failed to enrich idea ${idea.ProjectId}:`, err);
              return { ...idea, voteCount: 0, commentCount: 0, assessmentScore: 0 };
            }
          })
        );

        setIdeas(enrichedIdeas);
      }
    } catch (err: any) {
      console.error('Failed to fetch forum ideas:', err);
      setError(err.message || 'Failed to load ideas');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (projectId: string, newStatus: string) => {
    try {
      setActionLoading(true);
      const response = await fetch(`/projects/${projectId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });

      const data = await response.json();
      if (data.success) {
        // Refresh the list
        await fetchForumIdeas();
        setSelectedIdea(null);
      } else {
        setError(data.error || 'Failed to update status');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <div className={styles.loading}>Loading forum agenda...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Governance Forum Agenda</h1>
        <p className={styles.subtitle}>
          Review ideas ready for governance committee evaluation ({ideas.length} items)
        </p>
      </div>

      {error && (
        <div className={styles.error}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {ideas.length === 0 ? (
        <div className={styles.emptyState}>
          <p>No ideas currently ready for governance review.</p>
          <p>Ideas will appear here once they reach the ReadyForGovernance status.</p>
        </div>
      ) : (
        <div className={styles.ideasList}>
          {ideas.map(idea => (
            <div
              key={idea.ProjectId}
              className={`${styles.forumCard} ${selectedIdea?.ProjectId === idea.ProjectId ? styles.selected : ''}`}
              onClick={() => setSelectedIdea(idea)}
            >
              <div className={styles.cardHeader}>
                <div>
                  <h3 className={styles.ideaTitle}>{idea.Title}</h3>
                  <span className={styles.projectId}>{idea.ProjectId}</span>
                </div>
                <span className={styles.badge}>{idea.Domain}</span>
              </div>
              <p className={styles.description}>{idea.Description.substring(0, 120)}...</p>
              <div className={styles.stats}>
                <span className={styles.stat}>👥 {idea.voteCount || 0} votes</span>
                <span className={styles.stat}>💬 {idea.commentCount || 0} comments</span>
                <span className={styles.stat}>⭐ {idea.assessmentScore || 0} score</span>
                <span className={styles.stat}>📅 {new Date(idea.CreatedDate).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Panel */}
      {selectedIdea && (
        <div className={styles.detailPanel}>
          <button
            className={styles.closeButton}
            onClick={() => setSelectedIdea(null)}
            disabled={actionLoading}
          >
            ×
          </button>

          <div className={styles.detailContent}>
            <h2>{selectedIdea.Title}</h2>
            <p className={styles.projectId}>{selectedIdea.ProjectId}</p>

            <div className={styles.section}>
              <h3>Description</h3>
              <p>{selectedIdea.Description}</p>
            </div>

            <div className={styles.section}>
              <h3>Details</h3>
              <table className={styles.detailsTable}>
                <tbody>
                  <tr>
                    <td>Domain:</td>
                    <td>{selectedIdea.Domain}</td>
                  </tr>
                  <tr>
                    <td>Work Type:</td>
                    <td>{selectedIdea.WorkType}</td>
                  </tr>
                  <tr>
                    <td>Submitted By:</td>
                    <td>{selectedIdea.CreatedByName}</td>
                  </tr>
                  <tr>
                    <td>Votes:</td>
                    <td>{selectedIdea.voteCount || 0}</td>
                  </tr>
                  <tr>
                    <td>Comments:</td>
                    <td>{selectedIdea.commentCount || 0}</td>
                  </tr>
                  <tr>
                    <td>Assessment Score:</td>
                    <td>{selectedIdea.assessmentScore || 0}</td>
                  </tr>
                  <tr>
                    <td>Date Submitted:</td>
                    <td>{new Date(selectedIdea.CreatedDate).toLocaleDateString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className={styles.actions}>
              <button
                className={`${styles.button} ${styles.buttonPrimary}`}
                onClick={() => handleStatusChange(selectedIdea.ProjectId, 'UnderGovernanceReview')}
                disabled={actionLoading}
              >
                {actionLoading ? 'Updating...' : 'Proceed to Governance Review'}
              </button>
              <button
                className={`${styles.button} ${styles.buttonSecondary}`}
                onClick={() => handleStatusChange(selectedIdea.ProjectId, 'GatheringFeedback')}
                disabled={actionLoading}
              >
                {actionLoading ? 'Updating...' : 'Return to Domain Owner'}
              </button>
              <button
                className={`${styles.button} ${styles.buttonSecondary}`}
                onClick={() => setSelectedIdea(null)}
                disabled={actionLoading}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
